// Card and credit-line imports (every business, every bank).
// Banks disagree on signs for cards: some export charges as negative amounts,
// others as positive. On a liability account the person confirms which sign
// means "charge" (we preselect the one most rows use), so charges are always
// money out (more owed, expense) and payments/credits money in. Payments to
// the card can be saved as transfers instead of income.

export const LIABILITY_TYPES = ['credit_card', 'line_of_credit', 'loan', 'mortgage'];

export function isLiabilityAccount(account) {
  if (!account) return false;
  if (account.is_liability === true || account.is_liability === 'true') return true;
  if (account.is_liability === false || account.is_liability === 'false') return false;
  return LIABILITY_TYPES.includes(String(account.account_type || '').toLowerCase());
}

const signOf = (raw) => {
  const text = String(raw ?? '').trim();
  if (!text || !/[1-9]/.test(text)) return 0;
  return text.startsWith('-') ? -1 : 1;
};

// 'negative' or 'positive': the sign most non-zero rows use is taken as the
// charge sign (cards have many more charges than payments). Ties: negative.
export function guessChargeSign(rawAmounts) {
  let pos = 0;
  let neg = 0;
  for (const raw of rawAmounts || []) {
    const s = signOf(raw);
    if (s > 0) pos++;
    if (s < 0) neg++;
  }
  return pos > neg ? 'positive' : 'negative';
}

const DEBIT = /\b(debit|dr|withdrawal|withdrawl|charge|purchase)\b/i;
const CREDIT = /\b(credit|cr|deposit|payment)\b/i;

// Direction on a liability row. A debit/credit column wins when present;
// otherwise the confirmed charge sign decides.
export function liabilityRowDirection(rawAmount, chargeSign, indicator) {
  const s = signOf(rawAmount);
  if (s === 0) return null;
  const label = String(indicator || '');
  if (DEBIT.test(label) && !CREDIT.test(label)) return 'out';
  if (CREDIT.test(label) && !DEBIT.test(label)) return 'in';
  const chargeIsNegative = chargeSign !== 'positive';
  const isCharge = chargeIsNegative ? s < 0 : s > 0;
  return isCharge ? 'out' : 'in';
}

// Type on a liability row: refund/transfer labels are kept; charges are
// expenses; payments are transfers when asked, otherwise income.
export function liabilityRowType(label, direction, paymentsAsTransfers) {
  const lower = String(label || '').toLowerCase();
  if (lower.includes('refund') || lower.includes('return')) return 'refund';
  if (lower.includes('transfer')) return 'transfer';
  if (direction === null || direction === undefined) return null;
  if (direction === 'out') return 'expense';
  return paymentsAsTransfers ? 'transfer' : 'income';
}
